import { ref } from 'vue';
import {
  collection,
  addDoc,
  getDocs,
  query,
  where,
  updateDoc,
  doc,
  Timestamp,
  orderBy
} from 'firebase/firestore';
import { db } from '@/config/firebase';
import type { ITicket, ITicketGenerateRequest } from '@/interface/ITicket';

// LocalStorage key for ticket persistence
const TICKET_STORAGE_KEY = 'theyni_current_ticket';

// Initialize currentTicket from localStorage if available
const loadTicketFromStorage = (): ITicket | null => {
  try {
    const stored = localStorage.getItem(TICKET_STORAGE_KEY);
    if (stored) {
      const ticket = JSON.parse(stored);
      // Convert date strings back to Date objects
      if (ticket.createdAt) ticket.createdAt = new Date(ticket.createdAt);
      if (ticket.usedAt) ticket.usedAt = new Date(ticket.usedAt);

      // Check if ticket is still valid (not used)
      if (!ticket.used) {
        return ticket as ITicket;
      }
    }
  } catch (error) {
    console.error('Error loading ticket from storage:', error);
  }
  return null;
};

export const currentTicket = ref<ITicket | null>(loadTicketFromStorage());

export class TicketService {
  private collectionName = 'tickets';

  /**
   * Generate kode tiket unik
   */
  private generateTicketCode(): string {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
    let code = '';
    for (let i = 0; i < 12; i++) {
      code += chars.charAt(Math.floor(Math.random() * chars.length));
      if (i === 3 || i === 7) code += '-'; // Format: XXXX-XXXX-XXXX
    }
    return code;
  }

  /**
   * Buat tiket baru (batch)
   */
  async generateTickets(request: ITicketGenerateRequest): Promise<ITicket[]> {
    const tickets: ITicket[] = [];
    const now = new Date();

    // Generate regular tickets
    for (let i = 0; i < request.regularCount; i++) {
      const ticket: ITicket = {
        code: this.generateTicketCode(),
        type: 'regular',
        used: false,
        createdAt: now
      };

      const docRef = await addDoc(collection(db, this.collectionName), {
        ...ticket,
        createdAt: Timestamp.fromDate(ticket.createdAt)
      });

      tickets.push({ ...ticket, id: docRef.id });
    }

    // Generate special tickets
    for (let i = 0; i < request.specialCount; i++) {
      const ticket: ITicket = {
        code: this.generateTicketCode(),
        type: 'special',
        used: false,
        specialPrizeId: request.specialPrizeId,
        createdAt: now
      };

      const docRef = await addDoc(collection(db, this.collectionName), {
        ...ticket,
        createdAt: Timestamp.fromDate(ticket.createdAt)
      });

      tickets.push({ ...ticket, id: docRef.id });
    }

    return tickets;
  }

  /**
   * Validasi tiket berdasarkan kode
   */
  async validateTicket(code: string): Promise<{
    valid: boolean;
    ticket?: ITicket;
    message: string;
  }> {
    try {
      const q = query(collection(db, this.collectionName), where('code', '==', code.toUpperCase()));
      const querySnapshot = await getDocs(q);

      if (querySnapshot.empty) {
        return {
          valid: false,
          message: 'Tiket tidak ditemukan atau kode salah'
        };
      }

      const ticketDoc = querySnapshot.docs[0];
      const ticket = {
        id: ticketDoc.id,
        ...ticketDoc.data(),
        createdAt: ticketDoc.data().createdAt.toDate(),
        usedAt: ticketDoc.data().usedAt?.toDate()
      } as ITicket;

      if (ticket.used) {
        return {
          valid: false,
          ticket,
          message: 'Tiket sudah pernah digunakan'
        };
      }

      return {
        valid: true,
        ticket,
        message: 'Tiket valid!'
      };
    } catch (error) {
      console.error('Error validating ticket:', error);
      return {
        valid: false,
        message: 'Terjadi kesalahan saat validasi tiket'
      };
    }
  }

  /**
   * Tandai tiket sebagai sudah digunakan
   */
  async markTicketAsUsed(ticketId: string): Promise<void> {
    const ticketRef = doc(db, this.collectionName, ticketId);
    await updateDoc(ticketRef, {
      used: true,
      usedAt: Timestamp.fromDate(new Date())
    });
  }

  /**
   * Ambil semua tiket
   */
  async getAllTickets(): Promise<ITicket[]> {
    const q = query(collection(db, this.collectionName), orderBy('createdAt', 'desc'));
    const querySnapshot = await getDocs(q);

    return querySnapshot.docs.map((doc) => ({
      id: doc.id,
      ...doc.data(),
      createdAt: doc.data().createdAt.toDate(),
      usedAt: doc.data().usedAt?.toDate()
    })) as ITicket[];
  }

  /**
   * Set current active ticket
   */
  setCurrentTicket(ticket: ITicket | null) {
    currentTicket.value = ticket;

    // Save to localStorage for persistence across page refresh
    if (ticket) {
      try {
        localStorage.setItem(TICKET_STORAGE_KEY, JSON.stringify(ticket));
        console.log('Ticket saved to localStorage:', ticket.code);
      } catch (error) {
        console.error('Error saving ticket to localStorage:', error);
      }
    } else {
      localStorage.removeItem(TICKET_STORAGE_KEY);
    }
  }

  /**
   * Get current active ticket
   */
  getCurrentTicket(): ITicket | null {
    return currentTicket.value;
  }

  /**
   * Clear current ticket
   */
  clearCurrentTicket() {
    currentTicket.value = null;

    // Remove from localStorage
    try {
      localStorage.removeItem(TICKET_STORAGE_KEY);
      console.log('Ticket cleared from localStorage');
    } catch (error) {
      console.error('Error clearing ticket from localStorage:', error);
    }
  }
}
